/** @jsx jsx */
import { useRef, useState, useCallback } from 'react';
import { css, jsx } from '@emotion/core';
import { Flex } from '@rebass/grid/emotion';
import { Transition, animated } from 'react-spring';
import { animateScroll } from 'react-scroll';

import Thumb from './Thumb';
import VideoPlayer from './VideoPlayer';

const springConfig = { precision: 10 };

const animatedWrapperCss = css`
  position: absolute;
  z-index: 10;
`;

const overlayCss = shown => css`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  transition: opacity 500ms ease, visibility 0ms ${shown ? '0ms' : '1500ms'};
  opacity: ${shown ? 1 : 0};
  visibility: ${shown ? 'visible' : 'hidden'};
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 1;
`;

const Grid = ({ videos }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [transitions, setTransitions] = useState({});
  const wrapperRef = useRef(null);

  const preparePlayer = useCallback(({ url, box }) => {
    const { offsetHeight, offsetWidth } = wrapperRef.current;

    const { top, left, width, height } = box;

    const from = {
      opacity: 0,
      top,
      left,
      right: offsetWidth - left - width,
      height,
    };

    const enterHeight = (offsetWidth * 9) / 16;
    const enterTop =
      top + enterHeight > offsetHeight ? top + height - enterHeight : top;

    const enter = [
      { opacity: 1, config: { precision: 0.1 } },
      { left: 0, right: 0 },
      { top: enterTop, height: enterHeight },
    ];

    const leave = [
      { top, height },
      { left, right: offsetWidth - left - width },
      { opacity: 0, config: { precision: 0.1 } },
    ];

    // handling scroll to have player in viewport
    const { y } = wrapperRef.current.getBoundingClientRect();

    const scrollTop = y + enterTop;
    const scrollBottom = y + enterTop + enterHeight - window.innerHeight;

    if (scrollTop < 0) animateScroll.scrollMore(scrollTop, { duration: 500 });
    else if (scrollBottom > 0)
      animateScroll.scrollMore(scrollBottom, { duration: 500 });

    setVideoUrl(url);
    setTransitions({ from, enter, leave });
  }, []);

  const { from, enter, leave } = transitions;

  return (
    <section name="films" ref={wrapperRef} style={{ backgroundColor: '#000' }}>
      <Flex flexWrap="wrap">
        {videos.map(({ node: { originalId, ...thumb } }) => (
          <Thumb key={originalId} {...thumb} onClick={preparePlayer} />
        ))}
      </Flex>
      <div css={overlayCss(!!videoUrl)} />
      <Transition
        native
        items={videoUrl}
        from={from}
        enter={enter}
        leave={leave}
        config={springConfig}
      >
        {videoUrl =>
          videoUrl &&
          (props => (
            <animated.div css={animatedWrapperCss} style={props}>
              <VideoPlayer
                videoUrl={videoUrl}
                closeAction={() => setVideoUrl(null)}
              />
            </animated.div>
          ))
        }
      </Transition>
    </section>
  );
};

export default Grid;
